/**
 * Fit Right Finder Custom Component
 * 
 * Multi-step form to help users find the right saddle based on their measurements and preferences.
 * Converted from previous jQuery implementation in the previous theme.
 */
class FitRightFinder extends HTMLElement {
  connectedCallback() {
    this.pageIndex = 0;
    this.form = this.querySelector('#fitright-form');
    this.sections = this.querySelectorAll('.fitright-section--step');
    this.topAnchor = document.querySelector('#top-anchor');
    
    this.init();
  }

  init() {
    // Handle navigation buttons
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');
      
      switch (action) {
        case 'start':
          this.navigateToStep(1);
          break;
        case 'prev':
          this.navigatePrev();
          break;
        case 'next':
          this.navigateNext();
          break;
        case 'submit':
          this.handleSubmit(e);
          break;
      }
    });

    // Handle radio label clicks
    this.addEventListener('click', (e) => {
      const label = e.target.closest('.fitright-radio-label');
      if (!label) return;

      const radio = label.querySelector('.fitright-radio-input');
      if (radio) {
        // Uncheck all radios in the same group
        const groupName = radio.getAttribute('name');
        this.querySelectorAll(`input[name="${groupName}"]`).forEach(r => {
          r.checked = false;
        });
        // Check the clicked one
        radio.checked = true;
      }
    });
  }

  navigateToStep(stepIndex) {
    // Hide current section
    const currentSection = this.querySelector(`#section-${this.pageIndex}`);
    if (currentSection) {
      currentSection.classList.add('hidden');
      this.fadeOut(currentSection);
    }

    // Update page index
    this.pageIndex = stepIndex;

    // Show new section
    const newSection = this.querySelector(`#section-${this.pageIndex}`);
    if (newSection) {
      newSection.classList.remove('hidden');
      this.fadeIn(newSection);
      
      // Scroll to top
      this.scrollToTop();
    }
  }

  navigatePrev() {
    if (this.pageIndex > 0) {
      this.navigateToStep(this.pageIndex - 1);
    }
  }

  navigateNext() {
    this.navigateToStep(this.pageIndex + 1);
  }

  handleSubmit(e) {
    e.preventDefault();
    
    // Validate form
    if (!this.validateForm()) {
      const warning = this.querySelector('#fill_form_warning');
      if (warning) {
        warning.classList.remove('hidden');
      }
      return;
    }

    // Hide warning if visible
    const warning = this.querySelector('#fill_form_warning');
    if (warning) {
      warning.classList.add('hidden');
    }

    // Calculate saddle options
    this.calculateSaddleOptions();

    // Navigate to results
    this.navigateToStep(this.pageIndex + 1);
  }

  validateForm() {
    let isValid = true;

    // Check all required inputs
    const requiredInputs = this.form.querySelectorAll('input[required]');
    requiredInputs.forEach(input => {
      if (input.type === 'radio') {
        // For radio buttons, check if at least one in the group is checked
        const groupName = input.getAttribute('name');
        const checkedRadio = this.form.querySelector(`input[name="${groupName}"]:checked`);
        if (!checkedRadio) {
          isValid = false;
        }
      } else if (!input.value) {
        isValid = false;
      }
    });

    return isValid;
  }

  calculateSaddleOptions() {
    // Get wrist measurement
    const wristMeasurement = parseInt(this.form.querySelector('#form-wrist_measurement').value);
    console.log('Wrist measurement:', wristMeasurement);

    // Get riding position score
    let ridingPositionScore = 0;
    const ridingPosition = this.form.querySelector('input[name="riding-position"]:checked')?.value;
    console.log('Riding position:', ridingPosition);

    if (ridingPosition === 'leisure') {
      ridingPositionScore = 7;
    } else if (ridingPosition === 'performance') {
      ridingPositionScore = 0;
    } else if (ridingPosition === 'aggressive') {
      ridingPositionScore = -7;
    }
    console.log('Riding position score:', ridingPositionScore);

    // Get body type score
    let bodyTypeScore = 0;
    const bodyType = this.form.querySelector('input[name="body-shape"]:checked')?.value;
    console.log('Body type:', bodyType);

    if (bodyType === 'triangle') {
      bodyTypeScore = 15;
    }
    console.log('Body type score:', bodyTypeScore);

    // Calculate sit bone width
    const sitboneWidth = 2 * wristMeasurement + ridingPositionScore + bodyTypeScore;
    console.log('Sit bone width:', sitboneWidth);

    // Get padding preference
    const saddlePadding = this.form.querySelector('input[name="padding-thickness"]:checked')?.value;
    console.log('Saddle padding:', saddlePadding);

    // Update sit bone measurement display
    const sitBoneMeasurementEl = this.querySelector('#sit-bone-measurement');
    if (sitBoneMeasurementEl) {
      sitBoneMeasurementEl.textContent = sitboneWidth;
    }

    // Determine saddle width and range
    let saddleWidth = '';
    let sitBoneRange = '';

    if (sitboneWidth < 101) {
      saddleWidth = 'narrow';
      sitBoneRange = '69-101';
    } else if (sitboneWidth >= 101 && sitboneWidth < 131) {
      saddleWidth = 'medium';
      sitBoneRange = '102-130';
    } else if (sitboneWidth >= 131 && sitboneWidth <= 155) {
      saddleWidth = 'wide';
      sitBoneRange = '131-155';
    } else if (sitboneWidth > 155) {
      saddleWidth = 'wide';
      sitBoneRange = '155+';
    }

    // Update saddle width text and images
    const saddleWidthText = this.querySelector('#saddle-width-text');
    if (saddleWidthText) {
      saddleWidthText.textContent = saddleWidth.charAt(0).toUpperCase() + saddleWidth.slice(1);
    }

    // Update sit bone range
    const sitBoneRangeEl = this.querySelector('#site-bone-range');
    if (sitBoneRangeEl) {
      sitBoneRangeEl.textContent = sitBoneRange;
    }

    // Update padding text
    const saddlePaddingText = this.querySelector('#saddle-padding-text');
    if (saddlePaddingText) {
      saddlePaddingText.textContent = saddlePadding.charAt(0).toUpperCase() + saddlePadding.slice(1);
    }

    // Update saddle width images (show selected, hide others)
    this.querySelector('#saddle-image-wide')?.classList.toggle('selected', saddleWidth === 'wide');
    this.querySelector('#saddle-image-medium')?.classList.toggle('selected', saddleWidth === 'medium');
    this.querySelector('#saddle-image-narrow')?.classList.toggle('selected', saddleWidth === 'narrow');

    // Update padding images (show selected, hide others)
    this.querySelector('#padding-image-thick')?.classList.toggle('selected', saddlePadding === 'thick');
    this.querySelector('#padding-image-medium')?.classList.toggle('selected', saddlePadding === 'medium');
    this.querySelector('#padding-image-thin')?.classList.toggle('selected', saddlePadding === 'thin');

    // Show appropriate product collection
    const collectionId = `${saddleWidth}_${saddlePadding}_collection`;
    console.log('Showing collection:', collectionId);

    // Hide all collections first
    this.querySelectorAll('.fitright-products').forEach(collection => {
      collection.classList.add('hidden');
    });

    // Show the appropriate collection
    const targetCollection = this.querySelector(`#${collectionId}`);
    if (targetCollection) {
      targetCollection.classList.remove('hidden');
    }
  }

  fadeOut(element) {
    element.style.opacity = '1';
    element.style.transition = 'opacity 0.3s ease';
    setTimeout(() => {
      element.style.opacity = '0';
    }, 10);
  }

  fadeIn(element) {
    element.style.opacity = '0';
    element.style.transition = 'opacity 0.5s ease';
    setTimeout(() => {
      element.style.opacity = '1';
    }, 10);
  }

  scrollToTop() {
    if (this.topAnchor) {
      // Use smooth scroll behavior
      window.scrollTo({
        top: this.topAnchor.offsetTop,
        behavior: 'smooth'
      });
    }
  }
}

// Register custom element
if (!window.customElements.get('fit-right-finder')) {
  window.customElements.define('fit-right-finder', FitRightFinder);
}

